import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    MessageSquare, Search, Send, Paperclip, Edit2, X, Check,
    ChevronLeft, Users, Clock, Building2, Download, Loader2, FileText, ExternalLink, Eye, Plus, User
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EmployeeMessagesPage = () => {
    const location = useLocation();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [employeeStatus, setEmployeeStatus] = useState('active');

    // Edit message state
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState('');

    // File attachment state
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null); // For in-app file viewer
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const shouldScrollRef = useRef(true); // Track if we should auto-scroll

    // New message modal state
    const [showNewMessageModal, setShowNewMessageModal] = useState(false);
    const [messageableUsers, setMessageableUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [recipientSearch, setRecipientSearch] = useState('');
    const [selectedRecipient, setSelectedRecipient] = useState(null);
    const [initialMessage, setInitialMessage] = useState('');
    const [creatingConversation, setCreatingConversation] = useState(false);

    useEffect(() => {
        fetchConversations();
        fetchMessageableUsers(); // Fetch users early to map roles

        // Set up real-time polling for conversations list
        const conversationInterval = setInterval(() => {
            fetchConversations(true); // Silent refresh
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(conversationInterval);
    }, []);

    // Handle draft message from navigation (e.g. from AI escalation)
    useEffect(() => {
        if (location.state?.draft && messageableUsers.length > 0) {
            const { draft } = location.state;
            setShowNewMessageModal(true);
            setInitialMessage(draft.content || '');

            // Try to find a matching recipient
            if (draft.recipient_role) {
                // Find first user with matching role or default to first admin
                const recipientRegex = new RegExp(draft.recipient_role, 'i');
                const target = messageableUsers.find(u =>
                    (u.role_name && recipientRegex.test(u.role_name)) ||
                    (u.position && recipientRegex.test(u.position))
                ) || messageableUsers[0];

                if (target) {
                    setSelectedRecipient(target);
                }
            }

            // Clear state to prevent re-opening on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, messageableUsers]);

    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(selectedConversation.id);

            // Set up real-time polling for messages
            const messageInterval = setInterval(() => {
                fetchMessages(selectedConversation.id, true); // Silent refresh
            }, 3000); // Poll every 3 seconds

            return () => clearInterval(messageInterval);
        }
    }, [selectedConversation]);

    useEffect(() => {
        // Only scroll to bottom if shouldScrollRef is true
        if (shouldScrollRef.current) {
            scrollToBottom();
        }
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Handle scroll event to detect if user scrolled up
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        shouldScrollRef.current = isNearBottom;
    };

    const fetchConversations = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await api.get('/messages/employee/conversations');
            setConversations(response.data.conversations || []);
            setEmployeeStatus(response.data.employeeStatus || 'active');
        } catch (error) {
            console.error('Error fetching conversations:', error);
            if (!silent) toast.error('Failed to load conversations');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchMessages = async (conversationId, silent = false) => {
        if (!silent) setMessagesLoading(true);
        try {
            const response = await api.get(`/messages/conversations/${conversationId}/messages`);
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            if (!silent) toast.error('Failed to load messages');
        } finally {
            if (!silent) setMessagesLoading(false);
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && attachments.length === 0) return;
        if (!selectedConversation) return;

        const messageContent = newMessage;
        const messageAttachments = [...attachments];

        // Optimistically add message to UI
        const tempMessage = {
            id: `temp-${Date.now()}`,
            content: messageContent,
            attachments: messageAttachments.length > 0 ? messageAttachments : null,
            sender_type: 'employee',
            created_at: new Date().toISOString(),
            sending: true
        };

        setMessages([...messages, tempMessage]);
        setNewMessage('');
        setAttachments([]);
        setSending(true);

        try {
            const response = await api.post(`/messages/conversations/${selectedConversation.id}/messages`, {
                content: messageContent,
                attachments: messageAttachments.length > 0 ? messageAttachments : null
            });

            // Replace temp message with real one
            setMessages(prev => prev.map(m =>
                m.id === tempMessage.id ? response.data.message : m
            ));
            fetchConversations(true); // Update last message in list
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
            // Remove temp message on error
            setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        } finally {
            setSending(false);
        }
    };

    const handleEditMessage = async (messageId) => {
        if (!editContent.trim()) return;

        try {
            await api.put(`/messages/messages/${messageId}`, {
                content: editContent
            });

            setMessages(messages.map(m =>
                m.id === messageId ? { ...m, content: editContent, is_edited: true } : m
            ));
            setEditingMessage(null);
            setEditContent('');
            toast.success('Message updated');
        } catch (error) {
            console.error('Error editing message:', error);
            toast.error('Failed to edit message');
        }
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);

        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const response = await api.post('/messages/upload-attachment', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success && response.data.files) {
                setAttachments(prev => [...prev, ...response.data.files]);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast.error('Failed to upload files');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const fetchMessageableUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get('/messages/employee/admins');
            setMessageableUsers(response.data.users || []);
        } catch (error) {
            console.error('Error fetching messageable users:', error);
            toast.error('Failed to load contacts');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleOpenNewMessageModal = () => {
        setShowNewMessageModal(true);
        fetchMessageableUsers();
    };

    const handleCloseNewMessageModal = () => {
        setShowNewMessageModal(false);
        setSelectedRecipient(null);
        setRecipientSearch('');
        setInitialMessage('');
    };

    const handleCreateConversation = async () => {
        if (!selectedRecipient) {
            toast.error('Please select a recipient');
            return;
        }

        if (!initialMessage.trim()) {
            toast.error('Please enter a message');
            return;
        }

        setCreatingConversation(true);
        try {
            // Create conversation
            const convResponse = await api.post('/messages/employee/create-conversation', {
                recipientId: selectedRecipient.id,
                recipientType: selectedRecipient.type
            });

            const conversationId = convResponse.data.conversationId;

            // Send initial message
            await api.post(`/messages/conversations/${conversationId}/messages`, {
                content: initialMessage
            });

            // Refresh conversations
            await fetchConversations();

            // Select the new conversation
            const newConv = conversations.find(c => c.id === conversationId);
            if (newConv) {
                setSelectedConversation(newConv);
            } else {
                // If not in list yet, fetch conversations again
                const response = await api.get('/messages/employee/conversations');
                const convs = response.data.conversations || [];
                const foundConv = convs.find(c => c.id === conversationId);
                if (foundConv) {
                    setConversations(convs);
                    setSelectedConversation(foundConv);
                }
            }

            toast.success('Message sent!');
            handleCloseNewMessageModal();
        } catch (error) {
            console.error('Error creating conversation:', error);
            toast.error(error.response?.data?.message || 'Failed to send message');
        } finally {
            setCreatingConversation(false);
        }
    };

    const filteredMessageableUsers = messageableUsers.filter(user => {
        const searchLower = recipientSearch.toLowerCase();
        const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
        const email = user.email?.toLowerCase() || '';
        const position = user.position?.toLowerCase() || '';
        return fullName.includes(searchLower) || email.includes(searchLower) || position.includes(searchLower);
    });

    // Group users by category
    const groupedUsers = filteredMessageableUsers.reduce((acc, user) => {
        const category = user.category || 'Other';
        if (!acc[category]) acc[category] = [];
        acc[category].push(user);
        return acc;
    }, {});

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const getConversationName = (conversation) => {
        if (conversation.is_group) return conversation.name;
        // For direct messages, show the admin/company name
        const admin = conversation.participants?.find(p => p.type === 'admin');
        return admin?.name || 'Admin';
    };

    const getParticipantInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const filteredConversations = conversations.filter(conv => {
        const name = getConversationName(conv);
        return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Separate conversations by phase for display
    const preboardingConversations = filteredConversations.filter(c => c.phase === 'preboarding');
    const onboardingConversations = filteredConversations.filter(c => c.phase === 'onboarding');

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-6 py-4">
                <h1 className="text-2xl font-bold text-gray-800">Messages</h1>
                <p className="text-gray-600 text-sm">Your conversations with the admin team</p>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden bg-gray-50">
                {/* Conversations List */}
                <div className={`w-80 bg-white border-r border-gray-200 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'
                    }`}>
                    {/* New Message Button */}
                    <div className="p-4 border-b border-gray-100">
                        <button
                            onClick={handleOpenNewMessageModal}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium"
                        >
                            <Plus className="w-5 h-5" />
                            New Message
                        </button>
                    </div>

                    {/* Search */}
                    <div className="px-4 pb-4 border-b border-gray-100">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search conversations..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            />
                        </div>
                    </div>

                    {/* Conversations */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                        ) : filteredConversations.length === 0 ? (
                            <div className="p-6 text-center">
                                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm">No conversations yet</p>
                                <p className="text-gray-400 text-xs mt-1">
                                    Your admin will start a conversation with you
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Onboarding Conversations */}
                                {onboardingConversations.length > 0 && (
                                    <div>
                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                Onboarding
                                            </span>
                                        </div>
                                        {onboardingConversations.map((conv) => (
                                            <ConversationItem
                                                key={conv.id}
                                                conversation={conv}
                                                isSelected={selectedConversation?.id === conv.id}
                                                onClick={() => setSelectedConversation(conv)}
                                                getName={getConversationName}
                                                getInitials={getParticipantInitials}
                                                formatTime={formatTime}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Preboarding Conversations (History) */}
                                {preboardingConversations.length > 0 && (
                                    <div>
                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                                Preboarding {employeeStatus === 'active' && '(History)'}
                                            </span>
                                        </div>
                                        {preboardingConversations.map((conv) => (
                                            <ConversationItem
                                                key={conv.id}
                                                conversation={conv}
                                                isSelected={selectedConversation?.id === conv.id}
                                                onClick={() => setSelectedConversation(conv)}
                                                getName={getConversationName}
                                                getInitials={getParticipantInitials}
                                                formatTime={formatTime}
                                            />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'
                    }`}>
                    {selectedConversation ? (
                        <>
                            {/* Chat Header */}
                            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
                                <button
                                    onClick={() => setSelectedConversation(null)}
                                    className="md:hidden p-1 text-gray-500 hover:bg-gray-100 rounded"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${selectedConversation.is_group ? 'bg-purple-500' : 'bg-emerald-500'
                                    }`}>
                                    {selectedConversation.is_group ? (
                                        <Users className="w-5 h-5" />
                                    ) : (
                                        <Building2 className="w-5 h-5" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-800">
                                        {getConversationName(selectedConversation)}
                                    </h3>
                                    <div className="flex items-center gap-2">

                                        {selectedConversation.is_group && (
                                            <span className="text-xs text-gray-500">
                                                {selectedConversation.participants?.length || 0} members
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            <div onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messagesLoading ? (
                                    <div className="flex items-center justify-center h-32">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                                        <MessageSquare className="w-16 h-16 text-gray-300 mb-4" />
                                        <p>No messages yet</p>
                                        <p className="text-sm">Send a message to start the conversation</p>
                                    </div>
                                ) : (
                                    messages.map((message) => {
                                        const isEmployee = message.sender_type === 'employee';
                                        const isEditing = editingMessage === message.id;

                                        return (
                                            <div
                                                key={message.id}
                                                className={`flex ${isEmployee ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                                            >
                                                <div className={`max-w-[70%]`}>
                                                    {!isEmployee && (
                                                        <p className="text-xs font-medium text-gray-600 mb-1 ml-1">
                                                            {message.sender_name}
                                                        </p>
                                                    )}
                                                    <div className={`group relative rounded-2xl px-4 py-3 shadow-sm ${isEmployee
                                                        ? 'bg-primary text-white rounded-br-sm'
                                                        : 'bg-white border-2 border-gray-100 text-gray-800 rounded-bl-sm'
                                                        }`}>
                                                        {isEditing ? (
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={editContent}
                                                                    onChange={(e) => setEditContent(e.target.value)}
                                                                    className="flex-1 bg-transparent border-b border-white/50 focus:outline-none text-sm"
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={() => handleEditMessage(message.id)}
                                                                    className="p-1 hover:bg-white/20 rounded"
                                                                >
                                                                    <Check className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingMessage(null);
                                                                        setEditContent('');
                                                                    }}
                                                                    className="p-1 hover:bg-white/20 rounded"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isEmployee ? 'text-white' : 'text-gray-800'
                                                                    }`}>
                                                                    {message.content}
                                                                </p>
                                                                {message.attachments && message.attachments.length > 0 && (
                                                                    <div className="mt-3 space-y-2">
                                                                        {message.attachments.map((file, idx) => {
                                                                            // Format file size
                                                                            const formatSize = (bytes) => {
                                                                                if (!bytes) return '';
                                                                                if (bytes < 1024) return `${bytes} B`;
                                                                                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                                                                                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                                                                            };

                                                                            // Get file extension
                                                                            const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';

                                                                            // Check if file is previewable
                                                                            const isPreviewable = /\.(pdf|png|jpg|jpeg|gif|webp|svg|mp4|webm|mp3|wav)$/i.test(file.file_name || '');

                                                                            return (
                                                                                <div
                                                                                    key={idx}
                                                                                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isEmployee
                                                                                        ? 'bg-white/15 border border-white/20'
                                                                                        : 'bg-gray-100 border border-gray-200'
                                                                                        }`}
                                                                                >
                                                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isEmployee ? 'bg-white/20' : 'bg-primary/10'}`}>
                                                                                        <FileText className={`w-5 h-5 ${isEmployee ? 'text-white' : 'text-primary'}`} />
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className={`text-sm font-medium truncate ${isEmployee ? 'text-white' : 'text-gray-800'}`}>
                                                                                            {file.file_name}
                                                                                        </p>
                                                                                        <p className={`text-xs ${isEmployee ? 'text-white/60' : 'text-gray-500'}`}>
                                                                                            {ext} • {formatSize(file.file_size)}
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1">
                                                                                        {isPreviewable && (
                                                                                            <button
                                                                                                onClick={() => setPreviewFile(file)}
                                                                                                className={`p-2 rounded-lg transition-colors ${isEmployee
                                                                                                    ? 'hover:bg-white/20 text-white/70 hover:text-white'
                                                                                                    : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                                                                                                    }`}
                                                                                                title="Preview"
                                                                                            >
                                                                                                <Eye className="w-4 h-4" />
                                                                                            </button>
                                                                                        )}
                                                                                        <a
                                                                                            href={file.file_url}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className={`p-2 rounded-lg transition-colors ${isEmployee
                                                                                                ? 'hover:bg-white/20 text-white/70 hover:text-white'
                                                                                                : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                                                                                                }`}
                                                                                            title="Open in new tab"
                                                                                        >
                                                                                            <ExternalLink className="w-4 h-4" />
                                                                                        </a>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Edit action for employee's own messages */}
                                                        {isEmployee && !isEditing && (
                                                            <div className="absolute -left-10 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingMessage(message.id);
                                                                        setEditContent(message.content);
                                                                    }}
                                                                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`flex items-center gap-1 mt-1 text-xs ${isEmployee ? 'justify-end text-gray-400' : 'text-gray-500'
                                                        }`}>
                                                        <Clock className="w-3 h-3" />
                                                        <span>{formatTime(message.created_at)}</span>
                                                        {message.is_edited && <span className="italic">(edited)</span>}
                                                        {message.sending && (
                                                            <span className="flex items-center gap-1 text-gray-400 italic">
                                                                <div className="w-1 h-1 bg-gray-400 rounded-full animate-pulse"></div>
                                                                Sending...
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Attachments Preview */}
                            {attachments.length > 0 && (
                                <div className="bg-gray-50 border-t border-gray-200 px-4 py-2">
                                    <div className="flex flex-wrap gap-2">
                                        {attachments.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                                            >
                                                <Paperclip className="w-4 h-4 text-gray-400" />
                                                <span className="truncate max-w-[150px]">{file.file_name}</span>
                                                <button
                                                    onClick={() => removeAttachment(index)}
                                                    className="p-0.5 text-gray-400 hover:text-red-500"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Message Input */}
                            <form onSubmit={sendMessage} className="bg-white border-t border-gray-200 p-4">
                                <div className="flex items-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                                        title="Attach file"
                                    >
                                        {uploading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Paperclip className="w-5 h-5" />
                                        )}
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        multiple
                                        className="hidden"
                                    />
                                    <div className="flex-1">
                                        <textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Type a message..."
                                            rows={1}
                                            className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage(e);
                                                }
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                                        className="p-3 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {sending ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Send className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <MessageSquare className="w-12 h-12 text-gray-300" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-800 mb-1">Your Messages</h3>
                            <p className="text-sm text-center max-w-sm">
                                Select a conversation to view messages from your admin team
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* File Preview Modal */}
            {previewFile && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewFile(null)}>
                    <div className="bg-white rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-primary" />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-semibold text-gray-800 truncate">{previewFile.file_name}</p>
                                    <p className="text-xs text-gray-500">{previewFile.file_type}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <a
                                    href={previewFile.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Open in new tab"
                                >
                                    <ExternalLink className="w-5 h-5" />
                                </a>
                                <a
                                    href={previewFile.file_url}
                                    download={previewFile.file_name}
                                    className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Download"
                                >
                                    <Download className="w-5 h-5" />
                                </a>
                                <button
                                    onClick={() => setPreviewFile(null)}
                                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-auto p-4 bg-gray-50 flex items-center justify-center min-h-[300px]">
                            {/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(previewFile.file_name || '') ? (
                                <img
                                    src={previewFile.file_url}
                                    alt={previewFile.file_name}
                                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                                />
                            ) : /\.(mp4|webm)$/i.test(previewFile.file_name || '') ? (
                                <video
                                    src={previewFile.file_url}
                                    controls
                                    className="max-w-full max-h-[70vh] rounded-lg shadow-lg"
                                />
                            ) : /\.(mp3|wav)$/i.test(previewFile.file_name || '') ? (
                                <div className="bg-white p-8 rounded-xl shadow-lg">
                                    <audio src={previewFile.file_url} controls className="w-full min-w-[300px]" />
                                </div>
                            ) : /\.pdf$/i.test(previewFile.file_name || '') ? (
                                <iframe
                                    src={previewFile.file_url}
                                    className="w-full h-[70vh] rounded-lg shadow-lg bg-white"
                                    title={previewFile.file_name}
                                />
                            ) : (
                                <div className="text-center">
                                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-600 mb-4">Preview not available for this file type</p>
                                    <a
                                        href={previewFile.file_url}
                                        download={previewFile.file_name}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                                    >
                                        <Download className="w-4 h-4" />
                                        Download File
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* New Message Modal */}
            {showNewMessageModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={handleCloseNewMessageModal}>
                    <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <h2 className="text-xl font-bold text-gray-800">New Message</h2>
                            <button
                                onClick={handleCloseNewMessageModal}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {/* Recipient Selection */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    To:
                                </label>
                                {selectedRecipient ? (
                                    <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                            {selectedRecipient.type === 'admin' ? (
                                                <Building2 className="w-5 h-5 text-primary" />
                                            ) : (
                                                <User className="w-5 h-5 text-primary" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-800">
                                                {selectedRecipient.first_name} {selectedRecipient.last_name}
                                            </p>
                                            <p className="text-sm text-gray-500 truncate">
                                                {selectedRecipient.position || selectedRecipient.category}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setSelectedRecipient(null)}
                                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="relative mb-3">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Search for person..."
                                                value={recipientSearch}
                                                onChange={(e) => setRecipientSearch(e.target.value)}
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                            />
                                        </div>
                                        <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                                            {loadingUsers ? (
                                                <div className="flex items-center justify-center py-8">
                                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                                </div>
                                            ) : Object.keys(groupedUsers).length === 0 ? (
                                                <div className="p-6 text-center text-gray-500">
                                                    <p>No contacts available</p>
                                                    <p className="text-sm mt-1">Contact your admin for assistance</p>
                                                </div>
                                            ) : (
                                                Object.entries(groupedUsers).map(([category, users]) => (
                                                    <div key={category}>
                                                        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                                                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                                                {category}
                                                            </span>
                                                        </div>
                                                        {users.map((user) => (
                                                            <button
                                                                key={`${user.type}-${user.id}`}
                                                                onClick={() => setSelectedRecipient(user)}
                                                                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                                                            >
                                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.type === 'admin' ? 'bg-emerald-100' : 'bg-blue-100'
                                                                    }`}>
                                                                    {user.type === 'admin' ? (
                                                                        <Building2 className="w-5 h-5 text-emerald-600" />
                                                                    ) : (
                                                                        <User className="w-5 h-5 text-blue-600" />
                                                                    )}
                                                                </div>
                                                                <div className="flex-1 text-left min-w-0">
                                                                    <p className="font-medium text-gray-800">
                                                                        {user.first_name} {user.last_name}
                                                                    </p>
                                                                    <p className="text-sm text-gray-500 truncate">
                                                                        {user.position || user.email}
                                                                    </p>
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Message Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Message:
                                </label>
                                <textarea
                                    value={initialMessage}
                                    onChange={(e) => setInitialMessage(e.target.value)}
                                    placeholder="Type your message..."
                                    rows={6}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                            <button
                                onClick={handleCloseNewMessageModal}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateConversation}
                                disabled={!selectedRecipient || !initialMessage.trim() || creatingConversation}
                                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {creatingConversation ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send className="w-4 h-4" />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Conversation Item Component
const ConversationItem = ({ conversation, isSelected, onClick, getName, getInitials, formatTime }) => (
    <button
        onClick={onClick}
        className={`w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-50 ${isSelected ? 'bg-primary/5' : ''
            }`}
    >
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm ${conversation.is_group ? 'bg-purple-500' : 'bg-emerald-500'
            }`}>
            {conversation.is_group ? (
                <Users className="w-5 h-5" />
            ) : (
                <Building2 className="w-5 h-5" />
            )}
        </div>
        <div className="flex-1 min-w-0 text-left">
            <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-800 truncate">
                    {getName(conversation)}
                </h4>
                {conversation.last_message && (
                    <span className="text-xs text-gray-400">
                        {formatTime(conversation.last_message.created_at)}
                    </span>
                )}
            </div>
            <p className="text-sm text-gray-500 truncate">
                {conversation.last_message?.content || 'No messages yet'}
            </p>
            {conversation.unread_count > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-white bg-primary rounded-full mt-1">
                    {conversation.unread_count}
                </span>
            )}
        </div>
    </button>
);

export default EmployeeMessagesPage;

